import styles from './ApplicationStatusPage.module.scss'
import { Badge, Button, IconRegistry } from '@percent/lemonade'
import { useTranslation } from 'react-i18next'
import { GetManyValidationSubmission } from '@percent/cause-dashboard/services/applications/validationSubmission.types'
import { useAuthState, useCausesPortalAnalytics, useIntroModalState } from '@percent/cause-dashboard/common/hooks'
import { useEffect, useRef } from 'react'
import { HELP_LINK } from '@percent/cause-dashboard/constants/support'

type ApplicationStatusPageProps = {
  page: number
  nextPage?: () => void
  validationData?: GetManyValidationSubmission
  pagesVisible: boolean
  pagesCount: number
  height: number
  width: number
}

type BadgeProps = {
  variant: 'warning' | 'positive' | 'critical' | 'default'
  icon?: keyof typeof IconRegistry
  text: 'In progress' | 'Approved' | 'Rejected' | 'Unknown status'
}

export const statusMap: Record<string, BadgeProps> = {
  PENDING: {
    variant: 'warning',
    icon: 'clock',
    text: 'In progress'
  },
  APPROVED: {
    variant: 'positive',
    icon: 'check-badge',
    text: 'Approved'
  },
  REJECTED: {
    variant: 'critical',
    icon: 'reject',
    text: 'Rejected'
  },
  undefined: {
    variant: 'default',
    text: 'Unknown status'
  }
}

export function ApplicationStatusPage({
  nextPage,
  validationData,
  pagesVisible,
  page,
  pagesCount,
  height,
  width
}: Readonly<ApplicationStatusPageProps>) {
  const { t } = useTranslation()
  const { authState } = useAuthState()
  const { track } = useCausesPortalAnalytics()
  const trackedOpen = useRef(false)
  const { recordStatusCheckAction } = useIntroModalState()

  const handleNextPage = nextPage
    ? () => {
        recordStatusCheckAction(new Date())
        nextPage()
      }
    : undefined

  const formatDate = (date: string): string => {
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' }
    return new Date(date).toLocaleDateString('en-GB', options)
  }

  const getBadgeProps = (status: string): BadgeProps => {
    return statusMap[status] || statusMap.undefined
  }

  const shouldShowPageIndicator = !!(page && pagesCount && pagesCount > 1)

  const subtitle = validationData?.partnerName ?? ''

  const submittedAt = validationData?.submittedAt ?? ''
  const submissionDate = formatDate(submittedAt)

  const status = validationData?.status ?? ''
  const { variant, icon, text } = getBadgeProps(status)

  useEffect(() => {
    if (!trackedOpen.current) {
      track('Partner Application Status Modal Shown', {
        partnerName: subtitle,
        applicationStatus: validationData?.status,
        organisation: authState.claimOrganisation?.displayName,
        organisationId: authState.claimOrganisation?.id,
        accountId: authState.user?.id,
        shownOnHomepageModalPageNumber: page
      })
      trackedOpen.current = true
    }
  }, [
    track,
    subtitle,
    validationData?.status,
    authState.claimOrganisation?.displayName,
    authState.claimOrganisation?.id,
    authState.user?.id,
    page
  ])

  return (
    <div className={styles.modalCtn} style={{ height, width: '100%', maxWidth: width }}>
      <div className={`${styles.column} ${styles.leftColumn}`}>
        <div className={`${styles.leftCtn} ${!pagesVisible ? styles.lessPadding : ''}`}>
          {shouldShowPageIndicator && (
            <p className={styles.pageIndicator}>
              {page} {t('offersModal.page.of')} {pagesCount}
            </p>
          )}
          <p
            className={styles.title}
            style={{ marginTop: shouldShowPageIndicator ? '-16px' : '0' }}
            data-testid="title"
          >
            {t('modalPartnerApplication.title')}
          </p>
          <div className={styles.box} data-testid="box">
            <p className={styles.subtitle}> {subtitle} </p>
            <p> {t('modalPartnerApplication.date', { date: `${submissionDate}` })} </p>
            {icon ? (
              <Badge variant={variant} icon={icon}>
                {text}
              </Badge>
            ) : (
              <Badge variant={variant}>{text}</Badge>
            )}
          </div>
          <div className={styles.statusCtn}>
            {status === 'PENDING' && (
              <>
                <p className={styles.paragraph1}>{t('modalPartnerApplication.paragraph1')}</p>
                <p className={styles.paragraph2}>{t('modalPartnerApplication.paragraph2.title')}</p>
                <ol className={styles.list}>
                  <li className={`${styles.paragraph2} ${styles.bullet}`}>
                    {t('modalPartnerApplication.paragraph2.bullet1')}
                  </li>
                  <li className={`${styles.paragraph2} ${styles.bullet}`}>
                    {t('modalPartnerApplication.paragraph2.bullet2')}
                  </li>
                </ol>
              </>
            )}
          </div>
        </div>
        {handleNextPage ? (
          <div className={styles.buttonWrapper}>
            <Button
              data-testid="next-page-button"
              id={styles.nextPageButton}
              variant="secondary"
              onClick={handleNextPage}
            >
              {t('modalPartnerApplication.CTA.exploreOtherDiscounts')}
            </Button>
          </div>
        ) : (
          <div className={styles.footer}>
            <p data-testid="footer">
              {t('modalPartnerApplication.footer')}{' '}
              <a className={styles.link} href={HELP_LINK} data-testid="link">
                {t('modalPartnerApplication.contactUs')}
              </a>
            </p>
          </div>
        )}
      </div>
      <div className={`${styles.column} ${styles.rightColumn}`} />
    </div>
  )
}
